import { KeyboardEventHandler, useEffect, useMemo, useRef, useState } from 'react';

import { TextFieldProps } from '@purpur/library';

import { KeyboardCode } from '@telialt/common-constants/src/keyboard-codes';
import { KeyboardKey } from '@telialt/common-constants/src/keyboard-keys';
import { useFocusLost } from '@telialt/common-outside-click/src';
import { useComponentId, usePrevious } from '@telialt/utils/src/hooks';

import { SingleSelectComboBoxOption } from './model';
import {
    Chevron,
    Combobox,
    ComboboxLabel,
    HiddenInput,
    ListBox,
    ListBoxOption,
    SingleSelectComboBoxFieldset,
    SingleSelectComboBoxLabel,
    SingleSelectComboBoxLabelWrapper,
} from './SingleSelectComboBox.styles';
import { useGainFocus } from './useGainFocus';

type Props<O extends SingleSelectComboBoxOption> = Omit<TextFieldProps, 'onSelect'> & {
    options: O[];
    initialOptionId?: O['id'];
    onSelect?: (option: O) => void;
    dataTest?: string;
    isCompact?: boolean;
    hasMaxHeight?: boolean;
    inputId?: string;
    ariaLabelledBy?: string;
    closeDelay?: number;
};

const createID = (prefix: string) => ({
    LIST_BOX: `${prefix}-listBox`,
    COMBO_BOX: `${prefix}-comboBox`,
    CONFIRMED_VALUE_HOLDER: `${prefix}-confirmed-value`,
});

export const SingleSelectComboBox = <O extends SingleSelectComboBoxOption>({
    name: initialName,
    options = [],
    initialOptionId = options[0]?.id || '',
    onSelect,
    dataTest,
    className,
    isCompact,
    hasMaxHeight,
    disabled,
    inputId,
    startAdornment,
    label,
    ariaLabelledBy,
    closeDelay = 200,
}: Props<O>) => {
    const uuid = useComponentId();
    const name = initialName || `single-select-combo-box${uuid}`;
    const ID = createID(name);
    const [value, setValue] = useState(initialOptionId);
    const [confirmedValue, setConfirmedValue] = useState(initialOptionId);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const confirmedOption = useMemo(() => options.find((o) => o.id === confirmedValue), [confirmedValue, options]);

    const dropdownRef = useRef<HTMLUListElement>(null);
    const activeRadio = dropdownRef.current?.querySelector<HTMLLIElement>(`[id="${value}"]`);
    useGainFocus(activeRadio, isDropdownOpen);

    const [setDropdownClosingBoundaryRef] = useFocusLost(() => setIsDropdownOpen(false));

    const previousConfirmedValue = usePrevious(confirmedValue);

    useEffect(() => {
        setValue(initialOptionId);
        setConfirmedValue(initialOptionId);
    }, [initialOptionId]);

    useEffect(() => {
        if (confirmedOption?.id && previousConfirmedValue && previousConfirmedValue !== confirmedOption.id) {
            onSelect?.(confirmedOption);
        }
    }, [confirmedOption, previousConfirmedValue, onSelect]);

    const keyboardControlsForCombobox: KeyboardEventHandler = (event) => {
        if (disabled) {
            return;
        }
        if (
            event.key === KeyboardKey.Enter ||
            event.code === KeyboardCode.Space ||
            event.code === KeyboardCode.ArrowDown
        ) {
            event.preventDefault();
            setIsDropdownOpen(!isDropdownOpen);
        }
    };

    const keyboardControlsForListBox =
        (option: O, index: number): KeyboardEventHandler<HTMLLIElement> =>
        (event) => {
            if (disabled) {
                return;
            }
            if (event.key === KeyboardKey.Enter || event.code === KeyboardCode.Space) {
                event.preventDefault();
                setConfirmedValue(option.id);
                setIsDropdownOpen(false);
            }
            if (event.code === KeyboardCode.Escape) {
                setValue(confirmedValue);
                setIsDropdownOpen(false);
            }
            if (event.code === KeyboardCode.ArrowDown) {
                event.preventDefault();
                setValue((options[index + 1] || options[0]).id);
            }
            if (event.code === KeyboardCode.ArrowUp) {
                event.preventDefault();
                setValue((options[index - 1] || options[options.length - 1]).id);
            }
            if (event.code === KeyboardCode.Home) {
                event.preventDefault();
                setValue(options[0].id);
            }
            if (event.code === KeyboardCode.End) {
                event.preventDefault();
                setValue(options[options.length - 1].id);
            }
        };

    const selectOptionAndCloseDropdown = (option: O) => {
        setConfirmedValue(option.id);
        window?.setTimeout?.(() => setIsDropdownOpen(false), closeDelay);
    };

    const component = (
        <SingleSelectComboBoxFieldset
            ref={setDropdownClosingBoundaryRef}
            onSubmit={(event) => {
                event.preventDefault();
                setConfirmedValue(value);
                setIsDropdownOpen(false);
            }}
            className={className}
            data-test={dataTest}
        >
            <Combobox
                tabIndex={disabled ? -1 : 0}
                id={ID.COMBO_BOX}
                onKeyDown={keyboardControlsForCombobox}
                onClick={() => {
                    if (!disabled) {
                        setIsDropdownOpen(!isDropdownOpen);
                    }
                }}
                role="combobox"
                aria-label={confirmedOption?.ariaLabel}
                aria-autocomplete="none"
                aria-haspopup="listbox"
                aria-expanded={isDropdownOpen}
                aria-controls={ID.LIST_BOX}
                $isCompact={!!isCompact}
                $isDisabled={!!disabled}
                aria-disabled={disabled}
                aria-labelledby={ariaLabelledBy}
            >
                {startAdornment}
                <ComboboxLabel>{confirmedOption?.label}</ComboboxLabel>
                <HiddenInput
                    id={inputId || ID.CONFIRMED_VALUE_HOLDER}
                    name={ID.CONFIRMED_VALUE_HOLDER}
                    value={confirmedValue}
                    disabled={disabled}
                />
                <Chevron $isActive={isDropdownOpen} $isDisabled={!!disabled} />
            </Combobox>

            <ListBox
                id={ID.LIST_BOX}
                aria-expanded={isDropdownOpen}
                aria-activedescendant={ID.COMBO_BOX}
                $isVisible={isDropdownOpen}
                $hasMaxHeight={!!hasMaxHeight}
                ref={dropdownRef}
                role="listbox"
            >
                {options.map((option, index) => {
                    const isSelected = option.id === value;

                    return (
                        <ListBoxOption
                            key={option.id}
                            id={option.id}
                            data-test={option.dataTest}
                            tabIndex={0}
                            role="option"
                            aria-selected={isSelected}
                            aria-label={option.ariaLabel}
                            onFocus={() => setValue(option.id)}
                            onClick={() => selectOptionAndCloseDropdown(option)}
                            onKeyDown={keyboardControlsForListBox(option, index)}
                        >
                            {option.label}
                        </ListBoxOption>
                    );
                })}
            </ListBox>
        </SingleSelectComboBoxFieldset>
    );

    if (label) {
        return (
            <SingleSelectComboBoxLabelWrapper htmlFor={inputId} data-test={`label-${dataTest || name}`}>
                <SingleSelectComboBoxLabel>{label}</SingleSelectComboBoxLabel>
                {component}
            </SingleSelectComboBoxLabelWrapper>
        );
    }

    return component;
};
